import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { openCookiePreferences } from "@shared/lib/cookieConsent";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, Minus, Plus, X } from "@phosphor-icons/react";
import { RevealImage } from "../components/landing/RevealImage";
import { brandify } from "../components/brand";
import { RevealText, Reveal } from "../components/landing/RevealText";
import { PartnerLeadForm } from "../components/landing/PartnerLeadForm";
import { categories, formatNumber } from "../components/landing/demoData";
import { MerchantPaySection } from "../components/landing/MerchantPaySection";
import { RoPayBadge } from "../components/landing/RoPayBadge";

const PARTNER_EMAIL = "parteneri@suuper.ro";

export const Route = createFileRoute("/pentru-business")({
  head: () => ({
    meta: [
      { title: "suuper pentru business — Clienți noi și încasări instant prin RoPay" },
      {
        name: "description",
        content:
          "Oferă un preț membrilor suuper, încasează prin RoPay direct în contul firmei și primești un program de fidelitate care se umple singur. Fără cost de listare, fără aparat nou.",
      },
      {
        property: "og:title",
        content: "Clienți noi și încasări instant, fără buget de reclamă.",
      },
      {
        property: "og:description",
        content:
          "suuper aduce oamenii, RoPay mută banii direct în contul tău, iar fiecare plată devine o vizită numărată.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://suuper.ro/og-image.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: PentruBusinessPage,
});

/** Cum arată alternativele pe care un brand le are deja pe masă. */
const COMPARISON = [
  {
    label: "Reclamă plătită",
    tone: "negative" as const,
    points: [
      "Plătești pe afișare și pe click, indiferent dacă vinzi",
      "Costul pe client crește cu fiecare licitație",
      "Te oprești din plătit, se oprește și traficul",
    ],
  },
  {
    label: "Site de cupoane",
    tone: "negative" as const,
    points: [
      "Codul ajunge public și îți erodează prețul de listă",
      "Atrage vânători de reduceri, nu clienți care revin",
      "Nu știi cine a cumpărat și nici dacă se întoarce",
    ],
  },
  {
    label: "suuper",
    tone: "positive" as const,
    points: [
      "Prețul e vizibil doar membrilor autentificați",
      "Plătești prin ofertă, nu prin buget de media",
      "Tu stabilești plafonul, perioada și categoriile",
      "Încasezi prin RoPay și vezi cine revine",
    ],
  },
];

const PARTNER_STEPS = [
  {
    step: "01",
    title: "Stabilești oferta",
    text: "Tu pui prețul de membru, numărul maxim de utilizări și perioada. Nimic nu se activează fără acordul tău.",
  },
  {
    step: "02",
    title: "Ajunge la comunitate",
    text: "Oferta apare în aplicație, la membrii relevanți pentru categoria ta. Fără coduri publice, fără agregatoare.",
  },
  {
    step: "03",
    title: "Încasezi și vezi cine revine",
    text: "Clientul plătește prin RoPay, scanând QR-ul tău. Tu vezi plata confirmată, chitanța emisă și a câta vizită e pentru el.",
  },
];

const CONTROLS = [
  {
    title: "Prețul rămâne privat",
    text: "Nu ajunge pe Google și nu apare în comparatoare. Prețul tău public rămâne neatins.",
  },
  {
    title: "Plafon de utilizări",
    text: "Stabilești câte redeem-uri accepți, pe zi sau pe toată perioada campaniei.",
  },
  {
    title: "Perioadă și categorii",
    text: "Alegi când rulează oferta și pentru ce segment din comunitate e disponibilă.",
  },
  {
    title: "Zero cost de listare",
    text: "Nu plătești ca să fii în aplicație. Singurul cost e prețul pe care alegi să-l oferi.",
  },
];

const BUSINESS_FAQ = [
  {
    question: "Cât costă să fiu partener?",
    answer:
      "Listarea este gratuită. Nu există abonament, comision pe afișare sau taxă de intrare. Singurul cost pentru tine este diferența dintre prețul public și prețul pe care alegi să-l oferi membrilor.",
  },
  {
    question: "Îmi strică prețul public?",
    answer:
      "Nu. Prețul de membru este vizibil doar în aplicație, după autentificare. Nu este indexat de motoarele de căutare și nu ajunge în comparatoare de prețuri sau pe site-uri de cupoane.",
  },
  {
    question: "Ce se întâmplă dacă vin prea mulți clienți?",
    answer:
      "Stabilești de la început un plafon de utilizări, pe zi sau pe întreaga perioadă. Când e atins, oferta se oprește automat. Poți relua oricând, cu alte condiții.",
  },
  {
    question: "Ce fel de oferte funcționează cel mai bine?",
    answer:
      "Cele care au sens pentru un client care oricum ar fi cumpărat: un preț mai bun la produsul principal, un pachet, sau un beneficiu clar la prima achiziție. Reducerile simbolice de 5% nu conving pe nimeni să schimbe obiceiul.",
  },
  {
    question: "Comunitatea e destul de mare cât să merite?",
    answer:
      "Suntem la început și nu ascundem asta — numărul curent de membri este afișat mai sus. Partenerii care intră acum stabilesc condițiile primii și rămân vizibili pe măsură ce comunitatea crește.",
  },
  {
    question: "Cum se face decontarea?",
    answer:
      "Prețul de membru se aplică direct la tine. Dacă clientul plătește prin suuper, plata pleacă prin RoPay din contul lui în contul firmei tale, instant. suuper nu ține banii și nu reține comision din tranzacție în perioada pilot; plătești doar comisionul băncii tale pentru RoPay.",
  },
  {
    question: "Trebuie să am contract RoPay? Cum îl obțin?",
    answer:
      "RoPay se activează la banca la care ai contul firmei, dacă banca îl oferă comercianților (ING, BCR, Libra, CEC, UniCredit și altele), sau printr-un procesator de plăți partener. La ING, de exemplu, activarea e digitală, din internet banking. Îți spunem exact ce să ceri băncii tale și configurăm noi restul în suuper.",
  },
  {
    question: "Am nevoie de un aparat nou sau de o casă de marcat compatibilă?",
    answer:
      "Nu. Portalul suuper merge în browser, pe telefonul sau tableta pe care le ai deja. Tastezi suma, apare QR-ul, clientul scanează. Pentru mese sau tejghea poți afișa și un QR static tipărit.",
  },
];

function PentruBusinessPage() {
  const reduceMotion = useReducedMotion();
  const stats = useQuery(api.waitlist.getPublicStats);

  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToForm = useCallback(() => {
    document.getElementById("formular-partener")?.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  }, [reduceMotion]);

  return (
    <div className="min-h-screen bg-white">
      {/* ============ NAVBAR ============ */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/85 backdrop-blur-md rounded-b-[28px] sm:rounded-b-[36px]"
            : "bg-transparent"
        }`}
      >
        <nav
          className="container mx-auto max-w-6xl px-4 py-3.5 flex items-center justify-between gap-4"
          aria-label="Navigație principală"
        >
          <Link to="/join" className="flex items-center shrink-0">
            <img
              src="/suuper_logo.svg"
              alt="suuper"
              className="h-6 sm:h-7 w-auto"
            />
          </Link>

          <div className="flex items-center gap-1 sm:gap-2">
            <a
              href="#cum-functioneaza"
              className="hidden md:inline-flex px-3 py-2 text-sm font-medium text-text-light hover:text-text transition-colors"
            >
              Cum funcționează
            </a>
            <a
              href="#suuper-pay"
              className="hidden md:inline-flex px-3 py-2 text-sm font-medium text-text-light hover:text-text transition-colors"
            >
              Încasări RoPay
            </a>
            <Link
              to="/join"
              className="hidden md:inline-flex px-3 py-2 text-sm font-medium text-text-light hover:text-text transition-colors"
            >
              Pentru membri
            </Link>
            <Link
              to="/noutati"
              className="hidden md:inline-flex px-3 py-2 text-sm font-medium text-text-light hover:text-text transition-colors"
            >
              Noutăți
            </Link>
            <button
              type="button"
              onClick={scrollToForm}
              className="join-cta px-4 py-2.5 text-sm font-semibold bg-primary-gradient text-white rounded-xl whitespace-nowrap"
            >
              Devino partener
            </button>
          </div>
        </nav>
      </header>

      {/* ============ HERO ============ */}
      <section className="relative pt-28 pb-16 sm:pt-36 sm:pb-24 px-4">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/8 via-white to-white pointer-events-none" />

        <div className="container mx-auto max-w-6xl relative">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-14 lg:gap-16 items-center">
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center lg:text-left"
            >
              <h1
                className="font-bold text-text tracking-tight leading-[1.03] mb-6"
                style={{ fontSize: "clamp(2.5rem, 6vw, 4rem)" }}
              >
                Clienți noi,
                <br />
                fără buget de reclamă.
              </h1>

              <p className="text-lg sm:text-xl text-text-light leading-relaxed mb-6 max-w-xl mx-auto lg:mx-0">
                {brandify(
                  "Tu oferi un preț bun membrilor suuper. Noi îți aducem oameni care caută deja ce vinzi, îi lăsăm să plătească din aplicația băncii lor și îți numărăm vizitele ca să revină. Fără cost pe afișare, fără coduri care ajung publice."
                )}
              </p>

              <div className="mb-9 flex justify-center lg:justify-start">
                <RoPayBadge prefix="Încasezi prin" height={24} />
              </div>

              <button
                type="button"
                onClick={scrollToForm}
                className="join-cta w-full sm:w-auto inline-flex min-h-12 items-center justify-center gap-2 px-8 py-4 bg-primary-gradient text-white rounded-2xl font-semibold text-lg shadow-lg shadow-primary/25"
              >
                Devino partener
                <ArrowRight size={20} weight="bold" />
              </button>

              <p className="mt-4 text-sm text-text-muted">
                Fără cost de listare. Îți răspundem în două zile lucrătoare.
              </p>
            </motion.div>

            <motion.div
              initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="relative mx-auto w-full max-w-[460px] lg:max-w-none"
            >
              <div
                aria-hidden="true"
                className="absolute -inset-6 rounded-[3rem] bg-primary/15 blur-3xl"
              />
              <RevealImage
                name="suuper-card-usage"
                priority
                ratio="4/3"
                sizes="(max-width: 1024px) 90vw, 520px"
                className="relative rounded-[32px] shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ UNDE SUNTEM ============ */}
      <section className="px-4 pb-16 sm:pb-20">
        <div className="container mx-auto max-w-6xl">
          <Reveal className="rounded-[32px] border border-border bg-background p-8 sm:p-10">
            <div className="grid gap-8 sm:grid-cols-[auto_1fr] sm:items-center sm:gap-12">
              <div>
                <p className="text-[11px] font-bold tracking-[0.14em] text-primary mb-3">
                  UNDE SUNTEM ACUM
                </p>
                <p className="text-4xl sm:text-5xl font-bold text-text tracking-tight">
                  {stats ? formatNumber(stats.members) : "…"}
                  <span className="text-text-muted text-2xl sm:text-3xl">
                    {" "}
                    / {formatNumber(stats?.target ?? 10000)}
                  </span>
                </p>
                <p className="mt-2 text-text-light">
                  membri înscriși, din ținta pentru lansare
                </p>
              </div>

              <p className="text-lg text-text-light leading-relaxed">
                Suntem la început și preferăm să spunem asta direct, nu să
                inventăm cifre. Partenerii care intră acum negociază condițiile
                primii și rămân vizibili pe măsură ce comunitatea crește.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ COMPARAȚIE ============ */}
      <section className="py-20 sm:py-28 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <RevealText
            as="h2"
            text={"Reclama te costă\nînainte să vinzi."}
            highlight={["vinzi."]}
            className="text-center font-bold text-text tracking-tight leading-[1.05] text-[clamp(2rem,5.5vw,3.25rem)] mx-auto max-w-3xl"
          />

          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {COMPARISON.map((column, index) => (
              <Reveal
                key={column.label}
                delay={index * 0.1}
                className={`rounded-[28px] p-7 sm:p-8 ${
                  column.tone === "positive"
                    ? "bg-white border-2 border-primary/25 shadow-xl"
                    : "bg-white/60 border border-border"
                }`}
              >
                <p
                  className={`text-[11px] font-bold tracking-[0.14em] mb-6 ${
                    column.tone === "positive" ? "text-primary" : "text-text-muted"
                  }`}
                >
                  {brandify(column.label.toUpperCase())}
                </p>

                <ul className="space-y-4">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <span
                        aria-hidden="true"
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                          column.tone === "positive"
                            ? "bg-primary/10"
                            : "bg-text/5"
                        }`}
                      >
                        {column.tone === "positive" ? (
                          <Check size={12} weight="bold" className="text-primary" />
                        ) : (
                          <X size={12} weight="bold" className="text-text-muted" />
                        )}
                      </span>
                      <span
                        className={
                          column.tone === "positive"
                            ? "text-text"
                            : "text-text-light"
                        }
                      >
                        {brandify(point)}
                      </span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CUM FUNCȚIONEAZĂ ============ */}
      <section id="cum-functioneaza" className="py-20 sm:py-28 px-4 scroll-mt-24">
        <div className="container mx-auto max-w-6xl">
          <RevealText
            as="h2"
            text={"Trei pași\nși oferta e live."}
            highlight={["live."]}
            className="text-center font-bold text-text tracking-tight leading-[1.05] text-[clamp(2rem,5.5vw,3.25rem)] mx-auto max-w-3xl"
          />

          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {PARTNER_STEPS.map((item, index) => (
              <Reveal
                key={item.step}
                delay={index * 0.1}
                className="rounded-[28px] bg-white border border-border p-7 sm:p-8"
              >
                <p className="text-sm font-bold tracking-widest text-primary mb-5">
                  {item.step}
                </p>
                <p className="text-2xl font-bold text-text mb-3">{item.title}</p>
                <p className="text-text-light leading-relaxed">{item.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MerchantPaySection />

      {/* ============ CE CONTROLEZI ============ */}
      <section className="py-20 sm:py-28 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-14 lg:gap-16 items-center">
            <RevealImage
              name="friends-shopping-bags-street"
              ratio="4/5"
              sizes="(max-width: 1024px) 90vw, 420px"
              className="rounded-[32px] shadow-xl mx-auto w-full max-w-[400px] lg:max-w-none order-2 lg:order-1"
            />

            <div className="order-1 lg:order-2">
              <RevealText
                as="h2"
                text={"Tu decizi\ncât de departe merge."}
                highlight={["merge."]}
                className="font-bold text-text tracking-tight leading-[1.05] text-[clamp(2rem,5.5vw,3.25rem)]"
              />

              <Reveal delay={0.12}>
                <p className="mt-6 text-lg text-text-light leading-relaxed max-w-xl">
                  Grija oricărui brand cu un discount e că scapă de sub control.
                  De asta condițiile le pui tu, nu noi.
                </p>
              </Reveal>

              <div className="mt-10 grid sm:grid-cols-2 gap-5">
                {CONTROLS.map((item, index) => (
                  <Reveal
                    key={item.title}
                    delay={0.1 + index * 0.08}
                    className="rounded-3xl bg-white border border-border p-6"
                  >
                    <p className="text-lg font-bold text-text mb-2">
                      {item.title}
                    </p>
                    <p className="text-text-light leading-relaxed">
                      {item.text}
                    </p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CATEGORII ============ */}
      <section className="py-20 sm:py-28 px-4">
        <div className="container mx-auto max-w-5xl text-center">
          <RevealText
            as="h2"
            text={"Categoriile pe care\nle construim acum."}
            className="font-bold text-text tracking-tight leading-[1.05] text-[clamp(2rem,5.5vw,3.25rem)] mx-auto max-w-3xl"
          />

          <Reveal delay={0.12}>
            <p className="mt-6 text-lg text-text-light leading-relaxed max-w-2xl mx-auto">
              Dacă brandul tău nu se încadrează în niciuna, scrie-ne oricum.
              Deschidem categorii noi acolo unde membrii cer.
            </p>
          </Reveal>

          <ul className="mt-12 flex flex-wrap justify-center gap-3">
            {categories.map((category, index) => (
              <motion.li
                key={category.key}
                initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.04 }}
                className="rounded-full border border-border bg-white px-5 py-2.5 text-base font-semibold text-text"
              >
                {category.label}
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ FORMULAR ============ */}
      <section
        id="formular-partener"
        className="relative isolate overflow-hidden bg-accent scroll-mt-24"
      >
        <RevealImage
          name="women-cafe-terrace-coffee"
          fill
          sizes="100vw"
          parallax={false}
          imgClassName="object-[60%_45%]"
        />
        <div aria-hidden="true" className="absolute inset-0 bg-accent/60 lg:hidden" />
        <div
          aria-hidden="true"
          className="absolute inset-0 hidden lg:block bg-gradient-to-r from-accent/92 via-accent/55 to-transparent"
        />

        <div className="relative container mx-auto max-w-6xl px-4 py-24 sm:py-32">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-20 items-center">
            <div>
              <RevealText
                as="h2"
                text={"Hai să vedem\ndacă are sens."}
                className="font-bold text-white tracking-tight leading-[1.05] text-[clamp(2rem,5.5vw,3.25rem)]"
              />

              <Reveal delay={0.15}>
                <p className="mt-6 text-lg sm:text-xl text-white/75 leading-relaxed max-w-xl">
                  Spune-ne ce vinzi și cui. Îți răspundem cu o propunere
                  concretă de ofertă pentru membri — sau îți spunem sincer dacă
                  încă nu e momentul.
                </p>

                <p className="mt-8 text-white/60">
                  Preferi email?{" "}
                  <a
                    href={`mailto:${PARTNER_EMAIL}?subject=${encodeURIComponent(
                      "Parteneriat suuper"
                    )}`}
                    className="font-semibold text-white underline underline-offset-4 hover:no-underline"
                  >
                    {PARTNER_EMAIL}
                  </a>
                </p>
              </Reveal>
            </div>

            <PartnerLeadForm />
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="py-20 sm:py-28 px-4">
        <div className="container mx-auto max-w-3xl">
          <RevealText
            as="h2"
            text={"Întrebările care contează."}
            className="text-center font-bold text-text tracking-tight leading-[1.05] text-[clamp(2rem,5.5vw,3.25rem)] mb-12"
          />

          <ul className="space-y-3">
            {BUSINESS_FAQ.map((item, index) => {
              const isOpen = openFaq === index;
              return (
                <li
                  key={item.question}
                  className="rounded-3xl border border-border bg-white overflow-hidden"
                >
                  <h3>
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      aria-expanded={isOpen}
                      aria-controls={`faq-panel-${index}`}
                      className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                    >
                      <span className="text-lg font-bold text-text">
                        {brandify(item.question)}
                      </span>
                      <span
                        aria-hidden="true"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
                      >
                        {isOpen ? <Minus size={16} weight="bold" /> : <Plus size={16} weight="bold" />}
                      </span>
                    </button>
                  </h3>

                  {isOpen && (
                    <div id={`faq-panel-${index}`} className="px-6 pb-6 -mt-1">
                      <p className="text-text-light leading-relaxed">
                        {brandify(item.answer)}
                      </p>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="py-12 px-4 bg-primary rounded-t-[32px] sm:rounded-t-[44px]">
        <div className="container mx-auto max-w-6xl flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/join">
            <img
              src="/suuper_logo_white.svg"
              alt="suuper"
              className="h-7 w-auto"
            />
          </Link>

          <nav
            className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/85"
            aria-label="Linkuri footer"
          >
            <Link to="/join" className="hover:text-white transition-colors">
              Pentru membri
            </Link>
            <Link to="/noutati" className="hover:text-white transition-colors">
              Noutăți
            </Link>
            <Link to="/confidentialitate" className="hover:text-white transition-colors">
              Confidențialitate
            </Link>
            <Link to="/termeni" className="hover:text-white transition-colors">
              Termeni
            </Link>
            <button
              type="button"
              onClick={openCookiePreferences}
              className="hover:text-white transition-colors"
            >
              Setări cookie-uri
            </button>
            <a
              href={`mailto:${PARTNER_EMAIL}`}
              className="hover:text-white transition-colors"
            >
              {PARTNER_EMAIL}
            </a>
            <span className="text-white/70">
              Imagini ilustrative generate cu AI
            </span>
            <span className="text-white/80">© suuper</span>
          </nav>
        </div>
      </footer>
    </div>
  );
}
